import type React from "react";
import styled from "styled-components";
import { Button } from "@similarweb/ui-components";
import { Range, SliderHeader, SliderTitle } from "@similarweb/ui-components";
import { colorsPalettes } from "@similarweb/styles";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import type { Translate } from "app/filters/i18nFilter";
import { FlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { Flex } from "@similarweb/ui-components/foundations";
import type { DifficultyLevel, DifficultyLevelLimits } from "app/common/difficulty/difficulty.utils";
import { DIFFICULTY_LEVEL_WITH_LIMITS, getDifficultyLevel } from "app/common/difficulty/difficulty.utils";
import { setFont } from "@similarweb/styles";

// region getContent

const getDifficultyContent = (
    translate: Translate,
    {
        selectedRangeValue,
        rangeLimits,
        difficultyLevelsWithLimits = DIFFICULTY_LEVEL_WITH_LIMITS,
    }: {
        selectedRangeValue: [number, number];
        rangeLimits: [number, number];
        difficultyLevelsWithLimits: Record<DifficultyLevel, DifficultyLevelLimits>;
    },
) => {
    if (JSON.stringify(selectedRangeValue) === JSON.stringify(rangeLimits)) {
        return translate("keyword.difficulty.filter.any.difficulty");
    }
    const fromString = getDifficultyLevel(selectedRangeValue[0], difficultyLevelsWithLimits);
    const toString = getDifficultyLevel(selectedRangeValue[1], difficultyLevelsWithLimits);
    return translate("keyword.difficulty.filter.specific.difficulty", {
        fromNumber: selectedRangeValue[0],
        fromString,
        toNumber: selectedRangeValue[1],
        toString,
    });
};

const getDifficultyFilterStaticContent = (translate: Translate) => {
    return {
        labelContent: translate("keyword.difficulty.filter.label"),
        resetContent: translate("keyword.difficulty.filter.button.reset"),
        doneContent: translate("keyword.difficulty.filter.button.done"),
    };
};

// endregion

// region styled components

const DifficultyFilterWrapper = styled(FlexColumn)`
    width: 100%;
    height: 100%;
    ${SliderTitle} {
        ${setFont({ $size: 16 })}
    }
    ${SliderHeader} {
        margin-bottom: 13px;
    }
    .rc-slider .rc-slider-rail,
    .rc-slider .rc-slider-track {
        height: 4px;
    }
`;

const RangeWrapper = styled.div`
    padding: 30px 24px 25px;
`;

const HorizontalLine = styled.hr`
    border-top-color: ${colorsPalettes.carbon[100]};
    margin-top: 0;
    margin-bottom: 7px;
`;

const DifficultyBarButtonsWrapper = styled(Flex)`
    padding-right: 16px;
    justify-content: flex-end;
`;

const ResetButton = styled(Button)`
    margin-right: 20px;
`;

// endregion

type DifficultyFilterProps = {
    selectedRangeValue: [number, number];
    rangeLimits: [number, number];
    applyNewRange: (newFilterRange: [number, number]) => void;
    className?: string;
    difficultyLevelsWithLimits?: Record<DifficultyLevel, DifficultyLevelLimits>;
    shouldPresentResetButton: boolean;
    setFilterRangeValue: React.Dispatch<React.SetStateAction<[number, number]>>;
};

// region component

export const DifficultyFilter = ({
    selectedRangeValue,
    rangeLimits,
    applyNewRange,
    className,
    difficultyLevelsWithLimits = DIFFICULTY_LEVEL_WITH_LIMITS,
    setFilterRangeValue,
    shouldPresentResetButton,
}: DifficultyFilterProps) => {
    const translate = useTranslation();

    const { labelContent, resetContent, doneContent } = getDifficultyFilterStaticContent(translate);

    const difficultyContent = getDifficultyContent(translate, {
        selectedRangeValue,
        rangeLimits,
        difficultyLevelsWithLimits,
    });

    const setFilterRangeValueToInitial = () => {
        setFilterRangeValue(rangeLimits);
    };

    const applyNewRangeWithRangeCurrentValue = () => {
        applyNewRange(selectedRangeValue);
    };

    return (
        <DifficultyFilterWrapper className={className}>
            <RangeWrapper>
                <Range
                    min={rangeLimits[0]}
                    max={rangeLimits[1]}
                    value={selectedRangeValue}
                    onChange={setFilterRangeValue}
                    markInit={true}
                    title={labelContent}
                    description={difficultyContent}
                />
            </RangeWrapper>

            <HorizontalLine />
            <DifficultyBarButtonsWrapper>
                {shouldPresentResetButton ? (
                    <ResetButton
                        type={"flat"}
                        onClick={setFilterRangeValueToInitial}
                    >
                        {resetContent}
                    </ResetButton>
                ) : null}
                <Button onClick={applyNewRangeWithRangeCurrentValue}>{doneContent}</Button>
            </DifficultyBarButtonsWrapper>
        </DifficultyFilterWrapper>
    );
};

// endregion
